import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Button,
  Modal,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CustomAlert from "../../components/UI/CustomAlert/CustomAlert";
import { purchaseFeeList, purchaseFeeAdd, purchaseFeeEdit } from "../../util/API/loans";

function FeeDetailsPanel({ tableid, onClose, onRefresh }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Unified modal state for both Add and Edit
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTier, setCurrentTier] = useState(null);

  const [formData, setFormData] = useState({
    active: true,
    countryid: "*",
    currencycode: "*",
    endvalue: "9999999999999.00",
    fixedfeeconsumerexternal: "0.00",
    fixedfeeconsumerfranchisee: "0.00",
    fixedfeeconsumerreseller: "0.00",
    fixedfeeconsumersystem: "0.00",
    fixedfeemerchantexternal: "0.00",
    fixedfeemerchantfranchisee: "0.00",
    fixedfeemerchantreseller: "0.00",
    fixedfeemerchantsystem: "0.00",
    franchiseeid: "*",
    maxconsumerfees: "0.00",
    maxmerchantfees: "0.00",
    percentfeeconsumerexternal: "0.000",
    percentfeeconsumerfranchisee: "0.000",
    percentfeeconsumerreseller: "0.000",
    percentfeeconsumersystem: "0.000",
    percentfeemerchantexternal: "0.000",
    percentfeemerchantfranchisee: "0.000",
    percentfeemerchantreseller: "0.000",
    percentfeemerchantsystem: "0.000",
    piid: "*",
    remainingfeestorecol: "0",
    startvalue: "0.00",
    usetotalpercentage: "0",
  });

  const [submitting, setSubmitting] = useState(false);

  // Local CustomAlert
  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    header: "",
    message: "",
  });

  useEffect(() => {
    const fetchTiers = async () => {
      if (!tableid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await purchaseFeeList("", tableid);
        console.log("FeeDetailsPanel Response:", response);

        if (response.callStatus === "1") {
          const records = response.payload?.record || [];
          const feeArray = Array.isArray(records) ? records : records ? [records] : [];
          const processedTiers = feeArray
            .map(item => item?.Purchasefee)
            .filter(Boolean);

          setTiers(processedTiers);
        } else {
          setTiers([]);
        }
      } catch (error) {
        console.error("FeeDetailsPanel Error:", error);
        setTiers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTiers();
  }, [tableid]);

  const resetForm = () => {
    setFormData({
      active: true,
      countryid: "*",
      currencycode: "*",
      endvalue: "9999999999999.00",
      fixedfeeconsumerexternal: "0.00",
      fixedfeeconsumerfranchisee: "0.00",
      fixedfeeconsumerreseller: "0.00",
      fixedfeeconsumersystem: "0.00",
      fixedfeemerchantexternal: "0.00",
      fixedfeemerchantfranchisee: "0.00",
      fixedfeemerchantreseller: "0.00",
      fixedfeemerchantsystem: "0.00",
      franchiseeid: "*",
      maxconsumerfees: "0.00",
      maxmerchantfees: "0.00",
      percentfeeconsumerexternal: "0.000",
      percentfeeconsumerfranchisee: "0.000",
      percentfeeconsumerreseller: "0.000",
      percentfeeconsumersystem: "0.000",
      percentfeemerchantexternal: "0.000",
      percentfeemerchantfranchisee: "0.000",
      percentfeemerchantreseller: "0.000",
      percentfeemerchantsystem: "0.000",
      piid: "*",
      remainingfeestorecol: "0",
      startvalue: "0.00",
      usetotalpercentage: "0",
    });
  };

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentTier(null);
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEditModal = (tier) => {
    setIsEdit(true);
    setCurrentTier(tier);
    setFormData({
      active: tier.active === "1",
      countryid: tier.countryid,
      currencycode: tier.currencycode,
      endvalue: tier.endvalue,
      fixedfeeconsumerexternal: tier.fixedfeeconsumerexternal,
      fixedfeeconsumerfranchisee: tier.fixedfeeconsumerfranchisee,
      fixedfeeconsumerreseller: tier.fixedfeeconsumerreseller,
      fixedfeeconsumersystem: tier.fixedfeeconsumersystem,
      fixedfeemerchantexternal: tier.fixedfeemerchantexternal,
      fixedfeemerchantfranchisee: tier.fixedfeemerchantfranchisee,
      fixedfeemerchantreseller: tier.fixedfeemerchantreseller,
      fixedfeemerchantsystem: tier.fixedfeemerchantsystem,
      franchiseeid: tier.franchiseeid,
      maxconsumerfees: tier.maxconsumerfees,
      maxmerchantfees: tier.maxmerchantfees,
      percentfeeconsumerexternal: tier.percentfeeconsumerexternal,
      percentfeeconsumerfranchisee: tier.percentfeeconsumerfranchisee,
      percentfeeconsumerreseller: tier.percentfeeconsumerreseller,
      percentfeeconsumersystem: tier.percentfeeconsumersystem,
      percentfeemerchantexternal: tier.percentfeemerchantexternal,
      percentfeemerchantfranchisee: tier.percentfeemerchantfranchisee,
      percentfeemerchantreseller: tier.percentfeemerchantreseller,
      percentfeemerchantsystem: tier.percentfeemerchantsystem,
      piid: tier.piid,
      remainingfeestorecol: tier.remainingfeestorecol,
      startvalue: tier.startvalue,
      usetotalpercentage: tier.usetotalpercentage,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        location: "",
        tableid: parseInt(tableid),
        active: formData.active ? 1 : 0,
        countryid: formData.countryid || "*",
        currencycode: formData.currencycode || "*",
        endvalue: formData.endvalue || "9999999999999.00",
        fixedfeeconsumerexternal: formData.fixedfeeconsumerexternal || "0.00",
        fixedfeeconsumerfranchisee: formData.fixedfeeconsumerfranchisee || "0.00",
        fixedfeeconsumerreseller: formData.fixedfeeconsumerreseller || "0.00",
        fixedfeeconsumersystem: formData.fixedfeeconsumersystem || "0.00",
        fixedfeemerchantexternal: formData.fixedfeemerchantexternal || "0.00",
        fixedfeemerchantfranchisee: formData.fixedfeemerchantfranchisee || "0.00",
        fixedfeemerchantreseller: formData.fixedfeemerchantreseller || "0.00",
        fixedfeemerchantsystem: formData.fixedfeemerchantsystem || "0.00",
        franchiseeid: formData.franchiseeid || "*",
        maxconsumerfees: formData.maxconsumerfees || "0.00",
        maxmerchantfees: formData.maxmerchantfees || "0.00",
        percentfeeconsumerexternal: formData.percentfeeconsumerexternal || "0.000",
        percentfeeconsumerfranchisee: formData.percentfeeconsumerfranchisee || "0.000",
        percentfeeconsumerreseller: formData.percentfeeconsumerreseller || "0.000",
        percentfeeconsumersystem: formData.percentfeeconsumersystem || "0.000",
        percentfeemerchantexternal: formData.percentfeemerchantexternal || "0.000",
        percentfeemerchantfranchisee: formData.percentfeemerchantfranchisee || "0.000",
        percentfeemerchantreseller: formData.percentfeemerchantreseller || "0.000",
        percentfeemerchantsystem: formData.percentfeemerchantsystem || "0.000",
        piid: formData.piid || "*",
        remainingfeestorecol: formData.remainingfeestorecol || "0",
        startvalue: formData.startvalue || "0.00",
        usetotalpercentage: formData.usetotalpercentage || "0",
      };

      if (isEdit && currentTier?.feeid) {
        payload.feeid = currentTier.feeid;
      }

      const response = isEdit 
        ? await purchaseFeeEdit(payload)
        : await purchaseFeeAdd(payload);

      if (response.callStatus === "1") {
        setAlert({
          showAlert: true,
          severity: "success",
          header: "Success",
          message: isEdit ? "Tier updated successfully!" : "New tier added successfully!",
        });
        setModalOpen(false);
        if (onRefresh) onRefresh();
        onClose();
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          header: "Error",
          message: response.msg || "Operation failed",
        });
      }
    } catch (error) {
      console.error("Operation Error:", error);
      setAlert({
        showAlert: true,
        severity: "error",
        header: "Network Error",
        message: "Failed to save tier. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={60} thickness={5} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxHeight: "90vh", overflowY: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#5523D2" }}>
          Fee Table Details (ID: {tableid})
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Always show the Add New Tier button */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenAddModal}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
            textTransform: "none",
            fontWeight: "bold",
            py: 1.5,
          }}
        >
          Add New Tier
        </Button>
      </Box>

      {tiers.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="textSecondary">
            No fee tiers configured yet for this table.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Use the button above to add the first tier.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Fee Tiers ({tiers.length})
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Fee ID</strong></TableCell>
                  <TableCell><strong>Range</strong></TableCell>
                  <TableCell align="right"><strong>System</strong></TableCell>
                  <TableCell align="right"><strong>External</strong></TableCell>
                  <TableCell align="right"><strong>Franchisee</strong></TableCell>
                  <TableCell align="right"><strong>Reseller</strong></TableCell>
                  <TableCell align="right"><strong>Fixed System</strong></TableCell>
                  <TableCell align="right"><strong>Fixed External</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiers.map((tier) => (
                  <TableRow key={tier.feeid}>
                    <TableCell>{tier.feeid}</TableCell>
                    <TableCell>{tier.startvalue} – {tier.endvalue}</TableCell>
                    <TableCell align="right">{tier.percentfeemerchantsystem}%</TableCell>
                    <TableCell align="right">{tier.percentfeemerchantexternal}%</TableCell>
                    <TableCell align="right">{tier.percentfeemerchantfranchisee}%</TableCell>
                    <TableCell align="right">{tier.percentfeemerchantreseller}%</TableCell>
                    <TableCell align="right">{tier.fixedfeeconsumersystem}</TableCell>
                    <TableCell align="right">{tier.fixedfeeconsumerexternal}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenEditModal(tier)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Unified Add/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 900, md: 1200 },
            maxHeight: "95vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 4,
            p: { xs: 3, sm: 5 },
            overflowY: "auto",
          }}
        >
          <Typography variant="h5" sx={{ mb: 4, fontWeight: "bold", color: "#5523D2" }}>
            {isEdit ? `Edit Fee Tier (ID: ${currentTier?.feeid})` : "Add New Fee Tier"}
          </Typography>

          <Grid container spacing={3}>
            {/* Toggles — Vertically aligned */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Box>
              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.merchantfeeincludedinamount}
                      onChange={(e) => setFormData({ ...formData, merchantfeeincludedinamount: e.target.checked })}
                    />
                  }
                  label="Included in Loan Amount"
                />
              </Box>
            </Grid>

            {/* Currency & Country */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Currency Code"
                value={formData.currencycode}
                onChange={(e) => setFormData({ ...formData, currencycode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Country ID"
                value={formData.countryid}
                onChange={(e) => setFormData({ ...formData, countryid: e.target.value })}
              />
            </Grid>

            {/* Franchisee & PI ID */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Franchisee ID"
                value={formData.franchiseeid}
                onChange={(e) => setFormData({ ...formData, franchiseeid: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="PI ID"
                value={formData.piid}
                onChange={(e) => setFormData({ ...formData, piid: e.target.value })}
              />
            </Grid>

            {/* Value Range */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Value"
                value={formData.startvalue}
                onChange={(e) => setFormData({ ...formData, startvalue: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Value"
                value={formData.endvalue}
                onChange={(e) => setFormData({ ...formData, endvalue: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>

            {/* Merchant Percentage Split */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Merchant Percentage Split
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% System"
                value={formData.percentfeemerchantsystem}
                onChange={(e) => setFormData({ ...formData, percentfeemerchantsystem: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% External"
                value={formData.percentfeemerchantexternal}
                onChange={(e) => setFormData({ ...formData, percentfeemerchantexternal: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% Franchisee"
                value={formData.percentfeemerchantfranchisee}
                onChange={(e) => setFormData({ ...formData, percentfeemerchantfranchisee: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% Reseller"
                value={formData.percentfeemerchantreseller}
                onChange={(e) => setFormData({ ...formData, percentfeemerchantreseller: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>

            {/* Merchant Fixed Split */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Merchant Fixed Split
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed System"
                value={formData.fixedfeemerchantsystem}
                onChange={(e) => setFormData({ ...formData, fixedfeemerchantsystem: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed External"
                value={formData.fixedfeemerchantexternal}
                onChange={(e) => setFormData({ ...formData, fixedfeemerchantexternal: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed Franchisee"
                value={formData.fixedfeemerchantfranchisee}
                onChange={(e) => setFormData({ ...formData, fixedfeemerchantfranchisee: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed Reseller"
                value={formData.fixedfeemerchantreseller}
                onChange={(e) => setFormData({ ...formData, fixedfeemerchantreseller: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>

            {/* Consumer Percentage Split */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Consumer Percentage Split
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% System"
                value={formData.percentfeeconsumersystem}
                onChange={(e) => setFormData({ ...formData, percentfeeconsumersystem: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% External"
                value={formData.percentfeeconsumerexternal}
                onChange={(e) => setFormData({ ...formData, percentfeeconsumerexternal: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% Franchisee"
                value={formData.percentfeeconsumerfranchisee}
                onChange={(e) => setFormData({ ...formData, percentfeeconsumerfranchisee: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="% Reseller"
                value={formData.percentfeeconsumerreseller}
                onChange={(e) => setFormData({ ...formData, percentfeeconsumerreseller: e.target.value })}
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>

            {/* Consumer Fixed Split */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Consumer Fixed Split
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed System"
                value={formData.fixedfeeconsumersystem}
                onChange={(e) => setFormData({ ...formData, fixedfeeconsumersystem: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed External"
                value={formData.fixedfeeconsumerexternal}
                onChange={(e) => setFormData({ ...formData, fixedfeeconsumerexternal: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed Franchisee"
                value={formData.fixedfeeconsumerfranchisee}
                onChange={(e) => setFormData({ ...formData, fixedfeeconsumerfranchisee: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fixed Reseller"
                value={formData.fixedfeeconsumerreseller}
                onChange={(e) => setFormData({ ...formData, fixedfeeconsumerreseller: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>

            {/* Max Fees */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Consumer Fees"
                value={formData.maxconsumerfees}
                onChange={(e) => setFormData({ ...formData, maxconsumerfees: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Merchant Fees"
                value={formData.maxmerchantfees}
                onChange={(e) => setFormData({ ...formData, maxmerchantfees: e.target.value })}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ backgroundColor: "#1976d2" }}
            >
              {submitting ? "Saving..." : (isEdit ? "Save Changes" : "Add Tier")}
            </Button>
          </Box>
        </Box>
      </Modal>

      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={() => setAlert({ ...alert, showAlert: false })}
        autoclose={alert.severity === "success"}
      />
    </Box>
  );
}

export default FeeDetailsPanel;